import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface CustomerFormFieldProps {
  label: string;
  htmlFor: string;
  required?: boolean;
  /** One line under the control — what the field is for, or why it is read-only. */
  hint?: ReactNode;
  /** Inline error, announced via role="alert". */
  error?: string | null;
  children: ReactNode;
  className?: string;
}

/**
 * User Profile P2 — label + control + hint + error for customer forms. Wires
 * `htmlFor`, gives the hint and the error stable ids (`<id>-hint`, `<id>-error`)
 * so a control can point `aria-describedby` at them, and never hides the
 * control on error (the value must survive a validation failure).
 */
export function CustomerFormField({
  label,
  htmlFor,
  required = false,
  hint,
  error,
  children,
  className,
}: CustomerFormFieldProps) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <label htmlFor={htmlFor} className="text-sm font-medium text-customer-text">
        {label}
        {required && (
          <span className="ml-0.5 text-customer-danger-text" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {children}
      {hint && (
        <p id={`${htmlFor}-hint`} className="text-xs text-customer-text-secondary">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${htmlFor}-error`} role="alert" className="text-xs text-customer-danger-text">
          {error}
        </p>
      )}
    </div>
  );
}
